import { useId, useState, type FormEvent } from "react";
import { useCreateConnection } from "../../api/queries";
import type { Service } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import { computeTopology, DEFAULT_GATEWAY_PORT } from "./useConnectionTopology";
import "../../components/Form.css";

interface AddConnectionModalProps {
  open: boolean;
  projectId: string;
  fromServiceId: string;
  fromServiceName: string;
  /** Every other service in the project — the possible connection targets.
   * Doesn't include `fromServiceId` itself (a service can't connect to
   * itself — matches the mock/server refusal). */
  candidateServices: Service[];
  /** Node ids currently hosting each service in the project (this one and
   * every candidate target), keyed by service id — lets this modal preview
   * whether the connection about to be created would land cross-node
   * (ADR-0029 §2) BEFORE it's created, so the port-required hint below
   * applies at the moment it's actionable rather than only after the fact
   * on the read-only row. */
  nodeIdsByService: Map<string, string[]>;
  onClose: () => void;
}

/**
 * Declares "this service talks to that service" (ADR-0025 §2). Always
 * creates the connection outbound FROM the service the operator opened this
 * from — the reverse direction (something else depending on THIS service)
 * shows up read-only in the section below, added from the other service's
 * own page. `port`/`required` are declared topology only through Beta
 * Phase 1 — they don't yet enforce reachability (see Connection's doc
 * comment in types.ts) — the hint below says so rather than implying more
 * than the backend does.
 */
export function AddConnectionModal({
  open,
  projectId,
  fromServiceId,
  fromServiceName,
  candidateServices,
  nodeIdsByService,
  onClose,
}: AddConnectionModalProps) {
  const targetFieldId = useId();
  const portFieldId = useId();
  const requiredFieldId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateConnection(projectId);

  const [toService, setToService] = useState("");
  const [port, setPort] = useState("");
  const [required, setRequired] = useState(false);

  // Preview only — the server is the actual gate (ADR-0029 §2 requires a
  // port for a cross-node edge at manifest generation, not at connection
  // creation). "cross-node" here already implies both endpoints ARE
  // placed, which is when this preview can be trusted; "unplaced" is not
  // treated as a reason to require a port, since nothing is knowable yet.
  const likelyCrossNode =
    Boolean(toService) &&
    computeTopology(nodeIdsByService.get(fromServiceId) ?? [], nodeIdsByService.get(toService) ?? []) ===
      "cross-node";

  function handleClose() {
    setToService("");
    setPort("");
    setRequired(false);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!toService) return;
    const parsedPort = port.trim() ? Number.parseInt(port, 10) : undefined;
    mutation.mutate(
      { fromService: fromServiceId, toService, port: parsedPort, required },
      {
        onSuccess: (created) => {
          const targetName = candidateServices.find((s) => s.id === created.toService)?.name ?? "the target service";
          pushToast({ title: `Connected "${fromServiceName}" to "${targetName}".`, tone: "success" });
          handleClose();
        },
      },
    );
  }

  const parsedPort = port.trim() ? Number.parseInt(port, 10) : undefined;
  const portValid = port.trim() === "" || (Number.isInteger(parsedPort) && (parsedPort as number) >= 1 && (parsedPort as number) <= 65535);
  const portMissingForCrossNode = likelyCrossNode && port.trim() === "";
  const canSubmit = toService.length > 0 && portValid && !portMissingForCrossNode && !mutation.isPending;

  return (
    <Modal open={open} title="Add connection" onClose={handleClose} initialFocusId={targetFieldId}>
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't create this connection."}
          </p>
        )}

        <label className="form-field" htmlFor={targetFieldId}>
          <span>Talks to</span>
          <select id={targetFieldId} value={toService} onChange={(e) => setToService(e.target.value)} required>
            <option value="" disabled>
              {candidateServices.length === 0 ? "No other services in this project" : "Select a service"}
            </option>
            {candidateServices.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="form-field" htmlFor={portFieldId}>
          <span>{likelyCrossNode ? "Port (required — cross-machine)" : "Port (optional)"}</span>
          <input
            id={portFieldId}
            type="number"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => setPort(e.target.value)}
            required={likelyCrossNode}
          />
          {!portValid && <span className="form-field__error">Port must be between 1 and 65535.</span>}
          {portValid && portMissingForCrossNode && (
            <span className="form-field__error">
              "{fromServiceName}" and the selected service currently run on different nodes — a port is required so
              Kelpie's east-west gateway (TCP {DEFAULT_GATEWAY_PORT} by default) knows what to forward.
            </span>
          )}
        </label>

        <label className="form-field form-field--checkbox__row" htmlFor={requiredFieldId}>
          <input
            id={requiredFieldId}
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          <span>Required</span>
        </label>
        <p className="form-field__hint">
          Same-node pairs connect over the project's own network automatically. A pair on different nodes needs TCP{" "}
          {DEFAULT_GATEWAY_PORT} (Kelpie's east-west gateway, by default) open between those nodes' addresses
          (ADR-0029) — see the connection's status on the list below once it's created.
        </p>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Connecting…" : "Add connection"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
